// src/pages/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { resetPassword } from '../utils/api';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import muiTheme from '../theme/muiTheme';
import LockIcon from '@mui/icons-material/Lock';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import './ResetPassword.css';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token } = useParams();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Initialize theme
  useEffect(() => {
    try {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ResetPassword.js:29',message:'Component mounted',data:{hasMuiTheme:!!muiTheme,hasThemeProvider:typeof ThemeProvider !== 'undefined',hasToken:!!token},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'RP1'})}).catch(()=>{});
      // #endregion
      
      const savedTheme = localStorage.getItem('theme') || 'light';
      const darkMode = savedTheme === 'dark';
      setIsDarkMode(darkMode);
      document.documentElement.setAttribute('data-theme', savedTheme);
    } catch (error) {
      console.error('Error initializing ResetPassword:', error);
      setHasError(true);
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ResetPassword.js:38',message:'Error initializing component',data:{error:error.message,stack:error.stack?.substring(0,200)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'RP1'})}).catch(()=>{});
      // #endregion
    }
  }, [token]);

  // Monitor theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const theme = document.documentElement.getAttribute('data-theme') || 'light';
      setIsDarkMode(theme === 'dark');
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    const theme = newDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    if (name === 'password') {
      setPassword(value);
    } else if (name === 'confirmPassword') {
      setConfirmPassword(value);
    }
    setMessage(''); // Clear message when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!password || !confirmPassword) {
      setMessage('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setMessage('Password should be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await resetPassword(token, password);
      if (response.success) {
        setMessage('Password reset successful! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setMessage(response.message || 'Failed to reset password');
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Error resetting password. The link may have expired. Please request a new one.');
    } finally {
      setIsLoading(false);
    }
  };

  // Error boundary
  if (hasError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Something went wrong</h2>
        <p>Please refresh the page or try again later.</p>
      </div>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="reset-password-page">
        {/* Dark Mode Toggle Button */}
        <button
          onClick={toggleDarkMode}
          className="theme-toggle-btn"
          title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {isDarkMode ? (
            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        {/* Background Gradient Overlay */}
        <div className="reset-password-overlay"></div>

        {/* Main Container */}
        <div className="reset-password-container">
          {/* Header Section */}
          <div className="reset-password-header">
            <Link to="/" className="reset-password-logo">
              <span className="logo-text">WEHEAL</span>
            </Link>
            <div className="reset-password-subtitle">
              Set New Password
            </div>
          </div>

          {/* Card */}
          <Paper 
            elevation={0}
            className="reset-password-card"
            sx={{
              bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              border: isDarkMode ? '2px solid rgba(59, 130, 246, 0.3)' : '2px solid rgba(30, 64, 175, 0.1)',
              borderRadius: 3,
              p: { xs: 2, sm: 3 },
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Icon */}
            <Box className="reset-password-icon">
              <VpnKeyIcon sx={{ fontSize: 56, color: isDarkMode ? '#60A5FA' : '#1E40AF' }} />
            </Box>

            {/* Title */}
            <Typography 
              variant="h4" 
              className="reset-password-title"
              sx={{
                color: isDarkMode ? '#E0F2FE' : '#1E293B',
                fontWeight: 700,
                mb: 1,
                textAlign: 'center',
              }}
            >
              Set New Password
            </Typography>
            
            {/* Description */}
            <Typography 
              variant="body2" 
              className="reset-password-description"
              sx={{
                color: isDarkMode ? '#BFDBFE' : '#64748B',
                mb: 3,
                textAlign: 'center',
                fontSize: '0.875rem',
              }}
            >
              Enter your new password below. Make sure it's at least 6 characters long.
            </Typography>

            {/* Form */}
            <form onSubmit={handleSubmit} className="reset-password-form">
              {/* Password Input */}
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="New Password"
                placeholder="Enter new password"
                value={password}
                onChange={handlePasswordChange}
                required
                disabled={isLoading}
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: isDarkMode ? '#60A5FA' : '#1E40AF' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiInputBase-input': {
                    color: isDarkMode ? '#E0F2FE' : '#1E293B',
                  },
                  '& .MuiInputLabel-root': {
                    color: isDarkMode ? '#BFDBFE' : '#64748B',
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                    },
                    bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.8)',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: isDarkMode ? '#94A3B8' : '#9CA3AF',
                    opacity: 1,
                  },
                }}
              />

              {/* Confirm Password Input */}
              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                label="Confirm Password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={handlePasswordChange}
                required
                disabled={isLoading}
                autoComplete="new-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: isDarkMode ? '#60A5FA' : '#1E40AF' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiInputBase-input': {
                    color: isDarkMode ? '#E0F2FE' : '#1E293B',
                  },
                  '& .MuiInputLabel-root': {
                    color: isDarkMode ? '#BFDBFE' : '#64748B',
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
                    },
                    bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.8)',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: isDarkMode ? '#94A3B8' : '#9CA3AF',
                    opacity: 1,
                  },
                }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading || !password || !confirmPassword}
                className="reset-password-button"
                sx={{
                  py: 1.5,
                  mb: 2,
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)'
                    : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: isDarkMode 
                    ? '0 4px 12px rgba(59, 130, 246, 0.3)'
                    : '0 4px 12px rgba(30, 64, 175, 0.25)',
                  '&:hover': {
                    background: isDarkMode 
                      ? 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)'
                      : 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
                    boxShadow: isDarkMode 
                      ? '0 8px 20px rgba(59, 130, 246, 0.4)'
                      : '0 8px 20px rgba(30, 64, 175, 0.35)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                  },
                }}
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>

            {/* Message Display */}
            {message && (
              <Alert 
                severity={message.includes('successful') ? 'success' : 'error'}
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  bgcolor: isDarkMode 
                    ? (message.includes('successful') ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)')
                    : (message.includes('successful') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'),
                  color: isDarkMode 
                    ? (message.includes('successful') ? '#34D399' : '#F87171')
                    : (message.includes('successful') ? '#059669' : '#DC2626'),
                  border: isDarkMode 
                    ? (message.includes('successful') ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(239, 68, 68, 0.4)')
                    : (message.includes('successful') ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)'),
                }}
              >
                {message}
              </Alert>
            )}

            {/* Navigation Links */}
            <Box className="reset-password-links" sx={{ mt: 3, pt: 2, borderTop: isDarkMode ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid rgba(30, 64, 175, 0.1)' }}>
              <Link to="/login" className="reset-link">
                <ArrowBackIcon sx={{ fontSize: 18 }} />
                <span>Back to Login</span>
              </Link>
            </Box>
          </Paper>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default ResetPassword;
